// frontier-data.js — Atlas v3 "The Frontier" layer
// An EXPLORATORY overlay on the academic base graph. These are cross-domain
// bridges between resonance physics, consciousness-field models, plasma/ether
// concepts, ancient tech, and anomalous phenomena.
//
// METHOD: Sit in an idea without endorsing it. Every frontier node and every
// frontier bridge carries an evidence TIER (1-5) and shows its counter-evidence.
// The point is disaggregation — separating the defensible nugget from the leap —
// not validation. Tiers are honest editorial calls grounded in the literature
// (see refs); they are meant to be argued with and revised.
//
// TIER RUBRIC
//   T1 — Strong classical/mainstream support
//   T2 — Solid peer-reviewed interdisciplinary (not yet consensus)
//   T3 — Emerging/contested, multiple independent lines
//   T4 — Speculative but internally coherent, limited direct evidence
//   T5 — Exploratory / philosophical / high-strangeness
//
// All nodes here have layer:'frontier'. They are hidden until the user turns on
// "Show the frontier". Bridges may connect frontier->frontier OR frontier->core
// (the grounding bridges into the academic base are deliberate).

export const TIERS = {
  1: { name: 'Strong classical support', c: '#4FB477', short: 'T1' },
  2: { name: 'Solid interdisciplinary',  c: '#4FA3D1', short: 'T2' },
  3: { name: 'Emerging / contested',     c: '#C9A227', short: 'T3' },
  4: { name: 'Speculative but coherent', c: '#D98032', short: 'T4' },
  5: { name: 'Exploratory / hypothetical', c: '#C24B4B', short: 'T5' },
};

export const FLAG_LABELS = {
  'interpretive-leap': 'Interpretive leap',
  'single-source': 'Single source / lab',
  'small-sample': 'Small sample',
  'data-conflict': 'Conflicting data',
  'replication-failed': 'Failed replication',
  'advocacy-source': 'Advocacy-interested source',
  'mechanism-unknown': 'Mechanism unknown',
  'grey-literature': 'Grey literature origin',
};

// The frontier domain (a single category so the overlay is visually unified,
// but each node keeps its tier color on its ring).
export const FRONTIER_DOMAIN = { frontier: { name: 'The Frontier', c: '#8A7CC2' } };

// ---- Frontier nodes ----
// Each carries an evidence object. `tier` is the node's own grounded tier.
export const FRONTIER_NODES = [
  // — Resonance + Ancient Tech cluster —
  {
    id: 'cymatics', name: 'Cymatics', cat: 'frontier', hub: 0,
    desc: 'Vibration organizes matter into standing-wave patterns — Chladni figures. Textbook acoustics, often stretched toward "sacred geometry."',
    evidence: {
      tier: 1,
      claim: 'Vibrating surfaces at resonant frequencies produce predictable nodal patterns; particles collect at nodes. Fully explained by standing-wave physics — no extra principle needed.',
      why: 'Chladni/cymatic patterns are established, mathematically tractable acoustics. The leap to "encoded sacred information" is what is speculative, and that leap lives on a bridge, not here.',
      refs: [
        { t: 'Controlling the motion of multiple objects on a Chladni plate', s: 'Zhou et al., Nature Communications 2016', u: 'https://www.nature.com/articles/ncomms12764' },
        { t: 'Numerical Chladni figures', s: 'Müller, Eur. J. Physics 2013', u: 'https://arxiv.org/abs/1308.5523' },
      ],
      counter: 'Patterns depend entirely on plate shape, material, and excitation point — there is no universal "frequency → pattern" dictionary. Jenny\'s original work was observational and claimed nothing mystical.',
      flags: [],
      reviewed: '2026-06',
    },
  },
  {
    id: 'piezoelectric_stone', name: 'Piezoelectric Stone', cat: 'frontier', hub: 0,
    desc: 'Quartz under mechanical stress generates voltage. Granite is ~20-30% quartz — so stressed stone emits measurable EM.',
    evidence: {
      tier: 2,
      claim: 'Quartz is piezoelectric: under compression/shear/vibration it generates an electric potential. Quartz-bearing rock under tectonic or vibrational stress produces measurable EM emissions (kHz-MHz), used in earthquake-precursor research.',
      why: 'The physics is uncontested (T1-T2). It is rated T2 here, not T1, because the *natural-rock* case (random-orientation quartz domains partially cancel) is weaker than engineered single crystals. The megalith application is a separate, higher-tier bridge.',
      refs: [
        { t: 'Electromagnetic Emissions from Quartz Subjected to Shear Stress', s: 'Martinelli et al., Geosciences 2020', u: 'https://www.mdpi.com/2076-3263/10/4/140' },
        { t: 'Piezoelectric Pole Figure of a Vein Quartz Sample', s: 'Ghomshei et al., 1988', u: 'https://downloads.hindawi.com/archive/1988/621524.pdf' },
      ],
      counter: 'Random-orientation quartz in granite largely self-cancels; net effects are far weaker than oriented crystals. Ambient (non-seismic) voltages are far below any biological threshold.',
      flags: [],
      reviewed: '2026-06',
    },
  },
  {
    id: 'archaeoacoustics', name: 'Archaeoacoustics', cat: 'frontier', hub: 1,
    desc: 'Megalithic chambers resonate strongly at low frequencies. The popular "110 Hz brain effect" claim is shakier than it sounds.',
    evidence: {
      tier: 3,
      claim: 'Many megalithic/underground chambers (Newgrange, Wayland\'s Smithy, Hal Saflieni) have measurable resonance peaks, often in the male vocal range. Low-frequency auditory stimulation can shift regional brain activity.',
      why: 'Acoustic measurements are real and independently reported (T2-T3). But the famous "110 Hz" convergence traces to a 1995 grey-literature PEAR report; the rigorous 2017 Antiquity study of Hal Saflieni found dominant peaks at 41 and 72-76 Hz — NOT 110 Hz. The brain-effect link rests on lab tones, never measured in situ.',
      refs: [
        { t: 'An Archaeoacoustic Study of the Ħal Saflieni Hypogeum', s: 'Till, Antiquity 2017', u: 'https://www.cambridge.org/core/journals/antiquity/article/abs/an-archaeoacoustic-study-of-the-hal-saflieni-hypogeum-on-malta/2B530AEA46DEA5BF04D1C763725B3A34' },
        { t: 'Ancient Architectural Acoustic Resonance Patterns and Regional Brain Activity', s: 'Cook et al., Time and Mind 2008', u: 'https://www.tandfonline.com/doi/full/10.2752/175169608783489099' },
        { t: 'Psychoarchaeoacoustics for Understanding Ancient Minds', s: 'Escera et al., Front. Psychology 2020', u: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC7775382/' },
      ],
      counter: 'The "110 Hz" figure is overstated — the rigorous study found lower peaks. No study has measured EEG of people actually inside these chambers. Selection bias: only resonant sites get studied.',
      flags: ['grey-literature', 'data-conflict', 'interpretive-leap'],
      reviewed: '2026-06',
    },
  },
  {
    id: 'brainwave_entrainment', name: 'Brainwave Entrainment', cat: 'frontier', hub: 0,
    desc: 'Rhythmic sound can nudge cortical oscillations (the frequency-following response). Real but smaller and flakier than wellness marketing implies.',
    evidence: {
      tier: 2,
      claim: 'Auditory periodic stimulation can weakly entrain cortical oscillations. 40 Hz gamma stimulation has serious clinical interest (Alzheimer\'s). The effect is real but small, frequency- and individual-variable.',
      why: 'There is a genuine neural correlate (T2), but a 2023 systematic review found binaural-beat EEG effects small and inconsistent, with several rigorous null results under blinding.',
      refs: [
        { t: 'Binaural beats to entrain the brain? A systematic review', s: 'Heine et al., PLOS ONE 2023', u: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC10198548/' },
        { t: 'A Failure to Enhance EEG Power and Emotional Arousal', s: 'López-Caballero & Escera, Front. Hum. Neurosci. 2017', u: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC5694826/' },
      ],
      counter: 'Multiple blind protocols fail to replicate positive findings. "Neural coherence" as popularly described vastly overstates frequency-following. Binaural beats need headphones — not scalable to open chambers.',
      flags: ['data-conflict', 'replication-failed'],
      reviewed: '2026-06',
    },
  },

  // — Consciousness field + Observer cluster —
  {
    id: 'observer_effect', name: 'The Observer Problem', cat: 'frontier', hub: 1,
    desc: 'Quantum measurement is genuinely unresolved. "Consciousness collapses the wavefunction" is a minority interpretation, not a result.',
    evidence: {
      tier: 4,
      claim: 'The measurement problem is real physics (T1): QM predicts superpositions, we observe definite outcomes. But "observer" means any interaction creating an irreversible record — not a mind. Decoherence is the mainstream partial answer.',
      why: 'The physics core is solid, but the specific "consciousness causes collapse" reading (von Neumann-Wigner) is a minority philosophical position with no unique experimental support — hence T4 for that claim.',
      refs: [
        { t: 'Between myth and history: von Neumann on consciousness in QM', s: 'Laudisa, Stud. Hist. Phil. Sci. 2025', u: 'https://linkinghub.elsevier.com/retrieve/pii/S0039368125001244' },
        { t: 'A strong no-go theorem on the Wigner\'s friend paradox', s: 'Bong et al., Nature Physics 2020', u: 'https://arxiv.org/abs/1907.05607' },
        { t: 'The Quantum Measurement Problem: State of Play', s: 'Wallace, arXiv 2008', u: 'https://arxiv.org/abs/0712.0149' },
      ],
      counter: 'No experiment distinguishes a conscious from an unconscious "observer." Many empirically-equivalent interpretations need no consciousness. Wigner himself later abandoned the view.',
      flags: ['interpretive-leap'],
      reviewed: '2026-06',
    },
  },
  {
    id: 'magnetoreception', name: 'Human Magnetoreception', cat: 'frontier', hub: 0,
    desc: 'Magnetite crystals exist in the human brain, and one careful study found an EEG response to magnetic-field rotation.',
    evidence: {
      tier: 2,
      claim: 'Biogenic magnetite (Fe₃O₄) is definitively present in the human brain. A rigorously shielded 2019 study found a repeatable alpha-band EEG response to geomagnetic field rotations, with polarity-sensitivity ruling out induction artifacts.',
      why: 'Magnetite is established fact; the magnetoreception response is supported by careful EEG work but comes from a single lab and awaits independent replication — hence T2, not higher.',
      refs: [
        { t: 'Magnetite biomineralization in the human brain', s: 'Kirschvink et al., PNAS 1992', u: 'https://pnas.org/doi/full/10.1073/pnas.89.16.7683' },
        { t: 'Transduction of the Geomagnetic Field via Alpha-Band Activity', s: 'Wang et al., eNeuro 2019', u: 'https://www.eneuro.org/lookup/doi/10.1523/ENEURO.0483-18.2019' },
        { t: 'Magnetite pollution nanoparticles in the human brain', s: 'Maher et al., PNAS 2016', u: 'https://pnas.org/doi/full/10.1073/pnas.1605941113' },
      ],
      counter: 'Single-lab finding, replication pending. Much brain magnetite is combustion-pollution, not biogenic (Maher 2016). No magnetite-activated ion channel confirmed; mechanism unknown.',
      flags: ['single-source', 'mechanism-unknown'],
      reviewed: '2026-06',
    },
  },
  {
    id: 'geomagnetic_bio', name: 'Geomagnetic Biology', cat: 'frontier', hub: 1,
    desc: 'Geomagnetic storms correlate with reduced heart-rate variability in vulnerable people — but artifacts haunt this field.',
    evidence: {
      tier: 3,
      claim: 'Intense geomagnetic storms (Kp ≥ 5) are associated with statistically significant reductions in heart-rate variability in vulnerable populations (e.g. elderly men with cardiovascular disease).',
      why: 'The best study (n=809, 2022) is credible, but a careful replication showed most effects vanish after autocorrelation correction — so this sits at "emerging/contested," T3.',
      refs: [
        { t: 'Geomagnetic disturbances reduce HRV in the Normative Aging Study', s: 'Zilli Vieira et al., Sci. Total Environ. 2022', u: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC9233046/' },
        { t: 'Geomagnetic activity and HRV (replication w/ autocorrelation)', s: 'Mattoni et al., Eur. J. Appl. Physiol. 2020', u: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC11089572/' },
      ],
      counter: 'After proper autocorrelation correction, most prior correlations disappeared. Several supporting studies come from groups with advocacy interest. Effect sizes are clinically small; mechanism unknown.',
      flags: ['data-conflict', 'advocacy-source', 'mechanism-unknown'],
      reviewed: '2026-06',
    },
  },
  {
    id: 'neural_coherence', name: 'Neural Coherence', cat: 'frontier', hub: 1,
    desc: 'Large-scale phase synchrony in the brain tracks conscious experience. A mainstream neuroscience pillar — with an unsolved hard part.',
    evidence: {
      tier: 2,
      claim: 'Large-scale neural phase synchronization (especially gamma-band) is consistently associated with conscious perception, binding, and working memory. Disorder-of-consciousness patients show simplified, less coherent coordination.',
      why: 'Strong, mainstream empirical base (Varela, Singer, Demertzi) — T2. Rated here rather than T1 because causal sufficiency for consciousness remains genuinely debated.',
      refs: [
        { t: 'Human consciousness supported by dynamic complex coordination', s: 'Demertzi et al., Science Advances 2019', u: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC6365115/' },
        { t: 'Measuring phase synchrony in brain signals', s: 'Lachaux, Varela et al., Human Brain Mapping 1999', u: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC6873296/' },
      ],
      counter: 'Synchrony may be epiphenomenal, not the mechanism of binding. Gamma synchrony also appears in non-conscious processing. EEG volume conduction can fake phase synchrony.',
      flags: [],
      reviewed: '2026-06',
    },
  },
  {
    id: 'anomalous_cognition', name: 'Anomalous Experience & Field', cat: 'frontier', hub: 0,
    desc: 'Reports linking geomagnetic conditions to "high-strangeness" experiences. The strongest controlled tests come up empty.',
    evidence: {
      tier: 4,
      claim: 'Some statistical correlations have been reported between geomagnetic variables and self-reported anomalous experiences across several independent archival datasets. If real, the effect is small and the mechanism is entirely unknown.',
      why: 'Correlational archival signals exist, but the most controlled studies show no effect, and the mechanistic arm (Persinger\'s "God Helmet") failed double-blind replication — hence T4.',
      refs: [
        { t: 'The "Haunt" project (controlled EMF + infrasound test)', s: 'French et al., Cortex 2009', u: 'https://doi.org/10.1016/j.cortex.2007.10.011' },
        { t: 'Sensed presence predicted by suggestibility, not magnetic fields', s: 'Granqvist et al., Neurosci. Letters 2005', u: 'https://doi.org/10.1016/j.neulet.2004.10.057' },
        { t: 'Environmental analysis of public UAP sightings and sky view', s: 'Medina et al., Scientific Reports 2023', u: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC10721628/' },
      ],
      counter: 'Controlled Haunt experiment found suggestibility — not magnetics — predicted experiences. UAP reports track sky-viewing opportunity, not geomagnetics. Archival correlations are confounded by season, lunar phase, social contagion.',
      flags: ['replication-failed', 'data-conflict', 'mechanism-unknown'],
      reviewed: '2026-06',
    },
  },

  // — Plasma / Ether / Water cluster —
  {
    id: 'plasma_self_org', name: 'Plasma Self-Organization', cat: 'frontier', hub: 1,
    desc: 'Plasmas form filaments, double layers, and Birkeland currents. Real, measured physics — at magnetospheric scales.',
    evidence: {
      tier: 1,
      claim: 'Birkeland currents (field-aligned currents in magnetized plasma) are real, satellite-measured phenomena in Earth\'s magnetosphere. Plasma self-organizes into filaments and double layers; the universe is ~99% plasma by volume.',
      why: 'The core plasma physics is established and confirmed (T1). The overreach — "plasma cosmology replaces the Big Bang" — is a separate node/bridge rated far lower.',
      refs: [
        { t: 'Birkeland Current Density Observed by AMPERE', s: 'Anderson et al., JGR Space Physics 2022', u: 'https://onlinelibrary.wiley.com/doi/pdfdirect/10.1029/2021JA029801' },
        { t: "Birkeland's pioneering investigations of geomagnetic disturbances", s: 'Egeland & Burke, Hist. Geo-Space Sci. 2010', u: 'https://hgss.copernicus.org/articles/1/13/2010/' },
      ],
      counter: 'None for the magnetospheric physics. The danger is the banner: legitimate plasma science gets used to smuggle in "plasma cosmology" (see that node), which fails the CMB test.',
      flags: [],
      reviewed: '2026-06',
    },
  },
  {
    id: 'plasma_cosmology', name: 'Plasma Cosmology', cat: 'frontier', hub: 0,
    desc: 'The claim that electromagnetic/plasma processes — not gravity + dark matter — shape cosmic structure. Fails the key tests.',
    evidence: {
      tier: 5,
      claim: 'Plasma cosmology (Alfvén-Peratt-Lerner) proposes plasma/EM processes drive large-scale structure as an alternative to ΛCDM. The defensible residue: plasma and magnetic fields DO play a role in the real cosmic web.',
      why: 'As a cosmological replacement it is T5: it has never produced a quantitative model matching the CMB acoustic peaks, nucleosynthesis, or large-scale structure, and largely left mainstream venues after the mid-1990s.',
      refs: [
        { t: 'Plasma cosmology (mainstream critique + history)', s: 'Wikipedia (well-sourced overview)', u: 'https://en.wikipedia.org/wiki/Plasma_cosmology' },
        { t: 'Birkeland Currents in Cosmic Plasma', s: 'Peratt, Physics of the Plasma Universe (Springer)', u: 'https://link.springer.com/10.1007/978-1-4614-7819-5_2' },
      ],
      counter: 'Peebles (1993): the Alfvén-Klein model is inconsistent with CMB/X-ray isotropy. Predicts matter-antimatter symmetry → excessive gamma-ray background not observed. The cosmic web is reproduced by gravity-only simulations (IllustrisTNG, EAGLE).',
      flags: ['interpretive-leap', 'data-conflict'],
      reviewed: '2026-06',
    },
  },
  {
    id: 'ether_vacuum', name: 'Ether & the Quantum Vacuum', cat: 'frontier', hub: 1,
    desc: 'The quantum vacuum is real and structured (Casimir effect). Equating it with the 19th-century ether or "free energy" is an equivocation.',
    evidence: {
      tier: 4,
      claim: 'The QFT vacuum is not "nothing" — it produces measured effects (Casimir force, Lamb shift, electron anomalous moment). It is a structured field with fluctuation energy.',
      why: 'The vacuum-energy effects are T1 physics. This node sits at T4 because the *interesting bridge* — vacuum ↔ ether/inertia/free-energy — is where it gets speculative (Haisch-Rueda ZPF-inertia is peer-reviewed but unconfirmed; ZPE-as-free-energy is T5).',
      refs: [
        { t: 'Casimir Force: Vacuum fluctuations, zero-point energy', s: 'Xia et al., IEEE Antennas & Prop. 2021', u: 'https://ieeexplore.ieee.org/document/9428652/' },
        { t: 'Is zero-point energy physical? A toy model', s: 'Nikolic, Annals of Physics 2017', u: 'https://arxiv.org/abs/1702.03291' },
        { t: 'Inertia as a zero-point-field Lorentz force', s: 'Haisch, Rueda & Puthoff, Phys. Rev. A 1994', u: 'https://doi.org/10.1103/PhysRevA.49.678' },
      ],
      counter: 'The classical ether was ruled out by Michelson-Morley; the quantum vacuum is a categorically different concept, not a medium in absolute space. The Casimir effect can be derived without invoking ZPE (Nikolic 2017). The cosmological-constant problem (10¹²⁰ discrepancy) means our theories break down — not that ZPE powers the cosmos.',
      flags: ['interpretive-leap'],
      reviewed: '2026-06',
    },
  },
  {
    id: 'atmospheric_plasma', name: 'Atmospheric Plasma & UAP', cat: 'frontier', hub: 0,
    desc: 'Sprites and ball lightning are real luminous plasma events. They plausibly explain a subset of UAP reports — but not all.',
    evidence: {
      tier: 2,
      claim: 'Sprites, elves, and jets (transient luminous events) are confirmed atmospheric plasma phenomena, satellite-imaged. Ball lightning is widely reported and taken seriously by physicists. These plausibly explain a non-trivial fraction of night-time UAP reports.',
      why: 'TLEs are confirmed (T1); ball lightning has serious peer-reviewed physics. The UAP-explanation link is plausible but quantitatively unestablished — T2 overall.',
      refs: [
        { t: 'Relativistic-microwave theory of ball lightning', s: 'Wu, Scientific Reports 2016', u: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC4916449/' },
        { t: 'Genesis of column sprites', s: 'Marskar, arXiv 2024', u: 'https://arxiv.org/html/2310.08254v3' },
        { t: 'The Riddle of Ball Lightning: A Review', s: 'Rañada et al., Sci. World J. 2006', u: 'https://downloads.hindawi.com/journals/tswj/2006/948947.pdf' },
      ],
      counter: 'Ball lightning has no confirmed mechanism and has never been reliably captured by instruments. Sprites occur at 40-90 km and last milliseconds — poor fit for low, long-duration reports. The explainable fraction of UAP is unknown.',
      flags: ['mechanism-unknown'],
      reviewed: '2026-06',
    },
  },
  {
    id: 'structured_water', name: 'Structured / EZ Water', cat: 'frontier', hub: 0,
    desc: 'Water near hydrophilic surfaces forms an "exclusion zone" with odd properties. Real effect; the "fourth phase" interpretation is contested.',
    evidence: {
      tier: 3,
      claim: 'The exclusion zone — a region near hydrophilic surfaces that excludes colloidal particles, with distinct charge and spectroscopic properties — is real and independently replicated. It may be relevant to cell biology (hydration shells, ion channels).',
      why: 'The EZ phenomenon itself is solid (replicated). It sits at T3 because Pollack\'s "fourth phase of water" structural interpretation is seriously contested by a diffusiophoresis alternative, and the biology/health claims are T4-T5.',
      refs: [
        { t: 'Exclusion-Zone Phenomena in Water — A Critical Review', s: 'Elton et al., Int. J. Mol. Sci. 2020', u: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC7404113/' },
        { t: 'Exclusion-zone water inside and outside plant xylem', s: 'Wang & Pollack, Scientific Reports 2024', u: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC11130298/' },
      ],
      counter: 'A diffusiophoresis model (Schurr) can explain the core exclusion without a new water phase. NMR/neutron/X-ray studies don\'t consistently show the predicted long-range order. Most replications use Nafion (industrial polymer) — biological generalizability unclear. "Healing/information" claims have no peer-reviewed support.',
      flags: ['data-conflict', 'advocacy-source', 'interpretive-leap'],
      reviewed: '2026-06',
    },
  },
];

// ---- Frontier prerequisite edges (learning order within the frontier) ----
// [from, to, 'hard'|'soft']
export const FRONTIER_PREREQS = [
  ['cymatics', 'archaeoacoustics', 'soft'],
  ['piezoelectric_stone', 'archaeoacoustics', 'soft'],
  ['brainwave_entrainment', 'archaeoacoustics', 'soft'],
  ['neural_coherence', 'anomalous_cognition', 'soft'],
  ['magnetoreception', 'geomagnetic_bio', 'soft'],
  ['geomagnetic_bio', 'anomalous_cognition', 'soft'],
  ['observer_effect', 'anomalous_cognition', 'soft'],
  ['plasma_self_org', 'plasma_cosmology', 'hard'],
  ['plasma_self_org', 'atmospheric_plasma', 'soft'],
  ['ether_vacuum', 'plasma_cosmology', 'soft'],
];

// ---- Frontier bridges (THE HEART) ----
// Lateral connections, each carrying its OWN tier + evidence. Many connect a
// frontier node to a CORE academic node (grounding bridges) — these anchor the
// exploratory material to the established base. `grounding:true` marks a bridge
// that ties a frontier idea down to mainstream science.
export const FRONTIER_CROSS = [
  // — Grounding bridges into the academic base (anchor the frontier to consensus) —
  {
    a: 'cymatics', b: 'optics', tier: 1, grounding: true,
    evidence: {
      tier: 1,
      claim: 'Cymatics is standing-wave physics — the same wave mechanics underlying optics and acoustics. This bridge anchors it to the established base.',
      why: 'Direct, uncontested physical grounding.', refs: [], counter: '', flags: [], reviewed: '2026-06',
    },
  },
  {
    a: 'cymatics', b: 'music_theory', tier: 2, grounding: true,
    evidence: {
      tier: 2,
      claim: 'Resonance, harmonics, and nodal structure connect cymatic patterns to the mathematics of music — shared wave/frequency foundations.',
      why: 'Solid shared acoustic foundation.', refs: [], counter: 'The connection is structural (shared physics), not evidence that music "encodes" cymatic geometry.', flags: [], reviewed: '2026-06',
    },
  },
  {
    a: 'piezoelectric_stone', b: 'materials_science', tier: 1, grounding: true,
    evidence: {
      tier: 1,
      claim: 'Piezoelectricity is a core materials-science property of crystalline quartz — well-characterized and engineered into real devices.',
      why: 'Textbook materials science.', refs: [
        { t: 'Quantitative Examination of Piezoelectric/Seismoelectric Anomalies', s: 'Eppelbaum, Geosciences 2017', u: 'https://www.mdpi.com/2076-3263/7/3/90' },
      ], counter: '', flags: [], reviewed: '2026-06',
    },
  },
  {
    a: 'piezoelectric_stone', b: 'geology', tier: 2, grounding: true,
    evidence: {
      tier: 2,
      claim: 'Stressed quartz-bearing rock generates EM emissions studied as earthquake precursors — a real geophysical research program.',
      why: 'Peer-reviewed geophysics, though precursor reliability is itself debated.', refs: [
        { t: 'EM Emissions from Quartz Subjected to Shear Stress', s: 'Martinelli et al., Geosciences 2020', u: 'https://www.mdpi.com/2076-3263/10/4/140' },
      ], counter: 'Earthquake-precursor EM is an active, not settled, research area.', flags: [], reviewed: '2026-06',
    },
  },
  {
    a: 'archaeoacoustics', b: 'archaeology', tier: 2, grounding: true,
    evidence: {
      tier: 2,
      claim: 'Archaeoacoustics is a recognized subfield: studying the acoustic properties of ancient sites as part of their archaeological interpretation.',
      why: 'A legitimate, peer-reviewed archaeological subfield.', refs: [
        { t: 'Psychoarchaeoacoustics for Understanding Ancient Minds', s: 'Escera et al., Front. Psychology 2020', u: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC7775382/' },
      ], counter: 'Intentionality (did builders design for sound?) is interpretive, not established.', flags: ['interpretive-leap'], reviewed: '2026-06',
    },
  },
  {
    a: 'brainwave_entrainment', b: 'neuroscience', tier: 2, grounding: true,
    evidence: {
      tier: 2,
      claim: 'Auditory entrainment / the frequency-following response is a real, studied neuroscience phenomenon (e.g. 40 Hz gamma work in Alzheimer\'s).',
      why: 'Mainstream neuroscience, with the caveats noted on the node.', refs: [
        { t: 'Binaural beats systematic review', s: 'Heine et al., PLOS ONE 2023', u: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC10198548/' },
      ], counter: 'Effect sizes small; many null results under blinding.', flags: ['data-conflict'], reviewed: '2026-06',
    },
  },
  {
    a: 'observer_effect', b: 'quantum_mechanics', tier: 1, grounding: true,
    evidence: {
      tier: 1,
      claim: 'The measurement problem is a genuine, central open question in the foundations of quantum mechanics.',
      why: 'Unambiguously real physics.', refs: [
        { t: 'The Quantum Measurement Problem: State of Play', s: 'Wallace, arXiv 2008', u: 'https://arxiv.org/abs/0712.0149' },
      ], counter: '', flags: [], reviewed: '2026-06',
    },
  },
  {
    a: 'magnetoreception', b: 'neuroscience', tier: 2, grounding: true,
    evidence: {
      tier: 2,
      claim: 'Brain magnetite and EEG responses to magnetic fields are studied with standard neuroscience methods.',
      why: 'Grounded in real neuroscience; replication of the response is pending.', refs: [
        { t: 'Geomagnetic Field Transduction via Alpha-Band Activity', s: 'Wang et al., eNeuro 2019', u: 'https://www.eneuro.org/lookup/doi/10.1523/ENEURO.0483-18.2019' },
      ], counter: 'Single-lab, replication pending.', flags: ['single-source'], reviewed: '2026-06',
    },
  },
  {
    a: 'plasma_self_org', b: 'electromagnetism', tier: 1, grounding: true,
    evidence: {
      tier: 1,
      claim: 'Plasma physics and Birkeland currents are governed by electromagnetism + magnetohydrodynamics — established theory.',
      why: 'Core physics.', refs: [], counter: '', flags: [], reviewed: '2026-06',
    },
  },
  {
    a: 'plasma_self_org', b: 'astronomy', tier: 1, grounding: true,
    evidence: {
      tier: 1,
      claim: 'Field-aligned currents and plasma structures are observed throughout the magnetosphere and solar system by spacecraft.',
      why: 'Directly observed astrophysics.', refs: [
        { t: 'Birkeland Current Density Observed by AMPERE', s: 'Anderson et al., JGR 2022', u: 'https://onlinelibrary.wiley.com/doi/pdfdirect/10.1029/2021JA029801' },
      ], counter: '', flags: [], reviewed: '2026-06',
    },
  },
  {
    a: 'ether_vacuum', b: 'quantum_mechanics', tier: 1, grounding: true,
    evidence: {
      tier: 1,
      claim: 'The quantum vacuum and zero-point energy are well-defined in quantum field theory, with measured consequences (Casimir, Lamb shift).',
      why: 'Established QFT.', refs: [
        { t: 'Casimir Force review', s: 'Xia et al., IEEE 2021', u: 'https://ieeexplore.ieee.org/document/9428652/' },
      ], counter: '', flags: [], reviewed: '2026-06',
    },
  },
  {
    a: 'atmospheric_plasma', b: 'meteorology', tier: 1, grounding: true,
    evidence: {
      tier: 1,
      claim: 'Sprites and transient luminous events are atmospheric electrical phenomena tied to thunderstorm activity — established atmospheric science.',
      why: 'Confirmed, imaged phenomena.', refs: [
        { t: 'Genesis of column sprites', s: 'Marskar, arXiv 2024', u: 'https://arxiv.org/html/2310.08254v3' },
      ], counter: '', flags: [], reviewed: '2026-06',
    },
  },
  {
    a: 'neural_coherence', b: 'neuroscience', tier: 2, grounding: true,
    evidence: {
      tier: 2,
      claim: 'Phase synchrony as a neural correlate of consciousness is a mainstream (if debated) neuroscience research program.',
      why: 'Strong empirical base.', refs: [
        { t: 'Dynamic complex patterns of brain signal coordination', s: 'Demertzi et al., Science Advances 2019', u: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC6365115/' },
      ], counter: 'Causal sufficiency debated.', flags: [], reviewed: '2026-06',
    },
  },
  {
    a: 'structured_water', b: 'biochemistry', tier: 3, grounding: true,
    evidence: {
      tier: 3,
      claim: 'Interfacial (hydration-shell) water is genuinely important in biochemistry; the EZ may connect to this established area.',
      why: 'The grounded end is real biochemistry; the EZ link is emerging.', refs: [
        { t: 'Exclusion-Zone Phenomena — Critical Review', s: 'Elton et al., IJMS 2020', u: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC7404113/' },
      ], counter: 'Whether EZ specifically matters biologically is unresolved.', flags: ['data-conflict'], reviewed: '2026-06',
    },
  },

  // — The lateral leaps (frontier ↔ frontier): the non-obvious connections —
  {
    a: 'archaeoacoustics', b: 'brainwave_entrainment', tier: 3,
    evidence: {
      tier: 3,
      claim: 'IF chambers resonate in a range that drives cortical oscillations, ritual acoustics could plausibly shift participants\' brain states. The pieces are individually real; the in-situ combination is untested.',
      why: 'Each end is independently supported (T2-T3), but no study has measured entrainment from actual chamber resonance in situ — the bridge is a coherent hypothesis, not a demonstrated effect.',
      refs: [
        { t: 'Ancient Acoustic Resonance & Regional Brain Activity', s: 'Cook et al., Time and Mind 2008', u: 'https://www.tandfonline.com/doi/full/10.2752/175169608783489099' },
      ],
      counter: 'Chamber resonance is open-air, low-intensity sound — far from the headphone conditions where entrainment is (weakly) shown. The brain-effect studies used synthesized lab tones, not site recordings.',
      flags: ['interpretive-leap', 'data-conflict'],
      reviewed: '2026-06',
    },
  },
  {
    a: 'piezoelectric_stone', b: 'information_theory', tier: 4,
    evidence: {
      tier: 4,
      claim: 'Speculative: crystalline structures can in principle store/encode information (real, e.g. quartz data storage). The leap is to ancient megaliths functioning as information substrates.',
      why: 'Crystal data storage is real engineering, but applying it to megaliths is speculative with no supporting evidence — internally coherent, empirically empty.',
      refs: [],
      counter: 'No evidence any megalith stores or processes information. Modern crystal storage requires precise fabrication absent in natural stone.',
      flags: ['interpretive-leap'],
      reviewed: '2026-06',
    },
  },
  {
    a: 'cymatics', b: 'art_history', tier: 5,
    evidence: {
      tier: 5,
      claim: 'The high-strangeness pattern: that sacred/symbolic geometry across cultures "encodes" vibrational information visible in cymatics.',
      why: 'An aesthetic resonance, not an empirical claim. There is no evidence ancient artists knew cymatics or that symbolic geometry maps to specific frequencies — pure pattern-matching, flagged as such.',
      refs: [],
      counter: 'Same frequency on different surfaces yields entirely different patterns — there is no universal frequency→symbol dictionary. Visual similarity is not causation.',
      flags: ['interpretive-leap', 'single-source'],
      reviewed: '2026-06',
    },
  },
  {
    a: 'piezoelectric_stone', b: 'plasma_self_org', tier: 4,
    evidence: {
      tier: 4,
      claim: 'Speculative: large stressed stone structures generating EM could, in principle, couple to local atmospheric plasma/field conditions ("veil-thinning" environmental effects).',
      why: 'A chain of individually-real mechanisms (piezo EM, plasma coupling) assembled into an untested whole. No measurement supports the combined effect.',
      refs: [],
      counter: 'Ambient piezoelectric fields from megaliths would be vanishingly small — orders of magnitude below what could modulate atmospheric plasma.',
      flags: ['interpretive-leap'],
      reviewed: '2026-06',
    },
  },
  {
    a: 'observer_effect', b: 'neural_coherence', tier: 4,
    evidence: {
      tier: 4,
      claim: 'The philosophical-empirical overlap: field/coherence models of mind meeting the quantum measurement problem. Where "what is an observer?" meets "what binds experience?"',
      why: 'A genuine conceptual intersection that serious thinkers explore, but with no empirical bridge — quantum-mind proposals (Penrose-Hameroff) remain unconfirmed and contested.',
      refs: [
        { t: 'Consciousness and the measurement problem', s: 'Eren & Beni, Stud. Hist. Phil. Sci. 2025', u: 'https://linkinghub.elsevier.com/retrieve/pii/S0039368125001438' },
      ],
      counter: 'Decoherence dissolves most "quantum observer = mind" intuitions. Neural coherence is classical-scale EM, not quantum coherence. The two operate at vastly different scales.',
      flags: ['interpretive-leap', 'mechanism-unknown'],
      reviewed: '2026-06',
    },
  },
  {
    a: 'magnetoreception', b: 'anomalous_cognition', tier: 4,
    evidence: {
      tier: 4,
      claim: 'IF humans transduce geomagnetic fields, geomagnetic conditions could in principle modulate perception or unusual experiences.',
      why: 'Magnetoreception is T2 and anomalous-experience correlations are T4; chaining them is a coherent hypothesis but the controlled tests of the downstream claim came up null.',
      refs: [
        { t: 'Geomagnetic Field Transduction via Alpha-Band Activity', s: 'Wang et al., eNeuro 2019', u: 'https://www.eneuro.org/lookup/doi/10.1523/ENEURO.0483-18.2019' },
      ],
      counter: 'The magnetoreception response is below conscious threshold. Controlled "haunt" experiments found suggestibility, not fields, drove anomalous reports.',
      flags: ['interpretive-leap', 'replication-failed'],
      reviewed: '2026-06',
    },
  },
  {
    a: 'geomagnetic_bio', b: 'astronomy', tier: 2,
    evidence: {
      tier: 2,
      claim: 'Solar activity drives geomagnetic storms (space weather) — the upstream cause of any geomagnetic-biology effect is solidly understood solar physics.',
      why: 'The solar→geomagnetic link is established space physics (T1-T2); it grounds the upstream half of the geomagnetic-biology question.',
      refs: [],
      counter: 'Establishing the solar cause does not establish the biological effect downstream.',
      flags: [],
      reviewed: '2026-06',
    },
  },
  {
    a: 'plasma_cosmology', b: 'cosmology', tier: 5,
    evidence: {
      tier: 5,
      claim: 'Plasma cosmology positions itself as a rival to standard (ΛCDM) cosmology. The honest bridge is one of contrast, not support.',
      why: 'T5 as a cosmological claim: it fails the decisive tests (CMB acoustic peaks, nucleosynthesis) that ΛCDM passes. Included to show *why* the mainstream rejects it — disagreement made visible.',
      refs: [
        { t: 'Plasma cosmology (critique + history)', s: 'Wikipedia', u: 'https://en.wikipedia.org/wiki/Plasma_cosmology' },
      ],
      counter: 'No quantitative plasma-cosmology model reproduces the CMB power spectrum. ΛCDM does so to high precision.',
      flags: ['data-conflict', 'interpretive-leap'],
      reviewed: '2026-06',
    },
  },
  {
    a: 'ether_vacuum', b: 'relativity', tier: 4,
    evidence: {
      tier: 4,
      claim: 'The historical pivot: special relativity removed the need for a luminiferous ether; the quantum vacuum later reintroduced a structured "something" — but a different something.',
      why: 'A real and important conceptual history (T1 as history). Rated T4 as a *physical* bridge because conflating the two "ethers" is the central equivocation to resist.',
      refs: [],
      counter: 'Michelson-Morley ruled out the classical ether. The quantum vacuum is Lorentz-invariant and is NOT a medium in absolute space — the analogy misleads more than it informs.',
      flags: ['interpretive-leap'],
      reviewed: '2026-06',
    },
  },
  {
    a: 'atmospheric_plasma', b: 'anomalous_cognition', tier: 3,
    evidence: {
      tier: 3,
      claim: 'Atmospheric plasma (ball lightning, sprites) is a leading mundane explanation for a subset of anomalous/UAP sightings.',
      why: 'The most evidence-backed "prosaic" bridge in the anomalous cluster — plasma phenomena are confirmed and plausibly account for some reports, though the fraction is unknown.',
      refs: [
        { t: 'Environmental analysis of public UAP sightings', s: 'Medina et al., Scientific Reports 2023', u: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC10721628/' },
      ],
      counter: 'Cannot explain daytime, solid-radar-return, or long-duration cases. "Plasma" is sometimes used as an unfalsifiable catch-all.',
      flags: ['mechanism-unknown'],
      reviewed: '2026-06',
    },
  },
  {
    a: 'structured_water', b: 'neural_coherence', tier: 5,
    evidence: {
      tier: 5,
      claim: 'High-strangeness: that structured/EZ water in or around cells supports biological "coherence" or information processing relevant to consciousness.',
      why: 'T5 — chains a contested water model (T3) to a debated consciousness mechanism (T2) with no connecting evidence. Interesting to map; nothing supports it directly.',
      refs: [],
      counter: 'No evidence links EZ water to neural information processing. The biology-coherence claims around structured water lack peer-reviewed support and overlap with commercial pseudoscience.',
      flags: ['interpretive-leap', 'advocacy-source'],
      reviewed: '2026-06',
    },
  },
  {
    a: 'observer_effect', b: 'philosophy', tier: 3,
    evidence: {
      tier: 3,
      claim: 'The measurement problem is a live, serious topic in philosophy of physics — interpretations, the role of the observer, and what "measurement" means.',
      why: 'A legitimate, active philosophical research area (T2-T3) — distinct from the stronger physical "consciousness causes collapse" claim.',
      refs: [
        { t: 'Between myth and history: von Neumann on consciousness in QM', s: 'Laudisa, 2025', u: 'https://linkinghub.elsevier.com/retrieve/pii/S0039368125001244' },
      ],
      counter: 'Philosophical seriousness is not empirical support for any one interpretation.',
      flags: [],
      reviewed: '2026-06',
    },
  },
];

// New goal aliases that route into the frontier (only meaningful when the
// frontier layer is enabled).
export const FRONTIER_GOALS = [
  { label: 'Explore archaeoacoustics', targets: ['archaeoacoustics'], frontier: true },
  { label: 'Understand the observer problem', targets: ['observer_effect'], frontier: true },
  { label: 'Make sense of plasma & the cosmos', targets: ['plasma_self_org', 'plasma_cosmology'], frontier: true },
  { label: 'Investigate anomalous phenomena', targets: ['anomalous_cognition', 'atmospheric_plasma'], frontier: true },
];
